/**
 * @file useConversationActions.ts
 * @description Действия с диалогом (chat-window-design.md §4, §5 «Гонка
 * действий»; SC62, roles.md R4): «Взять себе», «Передать…», «Вернуть
 * оператору», «В архив», «Вернуть из архива».
 *
 * Каждое действие несёт `expectedAssignedUserId` — каким кабинет видел
 * ответственного. Сервер ответил 409 `chat_conversation_state_changed` —
 * «Пока вы выбирали, диалог передали …. Данные обновлены», карточка
 * перечитывается. После любого действия перечитываются список и число на
 * кнопке (один источник правды — сервер).
 */

import { useCallback, useState } from "react";

import { useNotify } from "../ui/NotifyProvider";
import { adminChat, type ConversationDetail, type TransferCandidate } from "../../lib/api-chat";
import { chatErrorText, raceDetails, raceText } from "./chat-errors";
import { CHAT_TEXT } from "./chat-texts";

export type ConversationActionKey = "take" | "transfer" | "returnToQueue" | "archive" | "unarchive";

interface Options {
  setDetail: (detail: ConversationDetail) => void;
  refreshDetail: () => Promise<void>;
  /** Логист вернул диалог — он пропал из его списка. */
  onReturned: () => void;
  /** Перечитать список и число на кнопке. */
  bumpData: () => void;
}

export function useConversationActions(detail: ConversationDetail | null, options: Options) {
  const notify = useNotify();
  const [busy, setBusy] = useState<ConversationActionKey | null>(null);
  const { setDetail, refreshDetail, onReturned, bumpData } = options;

  const run = useCallback(
    async (key: ConversationActionKey, call: () => Promise<ConversationDetail | null>, success: string, fallback: string) => {
      if (!detail || busy) return false;
      setBusy(key);
      try {
        const card = await call();
        if (card) setDetail(card);
        notify.success(success);
        return true;
      } catch (error) {
        const race = raceDetails(error);
        if (race) {
          notify.warning(raceText(race));
          await refreshDetail();
        } else {
          notify.error(chatErrorText(error, fallback));
        }
        return false;
      } finally {
        setBusy(null);
        bumpData();
      }
    },
    [detail, busy, notify, setDetail, refreshDetail, bumpData],
  );

  const guard = { expectedAssignedUserId: detail?.assignedUserId ?? null };
  const id = detail?.id ?? "";

  const take = useCallback(
    () => run("take", () => adminChat.take(id, guard), CHAT_TEXT.toastTaken, "Не удалось взять диалог. Попробуйте ещё раз"),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- guard собирается из detail
    [run, id, detail?.assignedUserId],
  );

  const transfer = useCallback(
    (to: TransferCandidate) =>
      run("transfer", () => adminChat.transfer(id, to.id, guard), CHAT_TEXT.toastTransferred(to.name), CHAT_TEXT.toastTransferFailed),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- guard собирается из detail
    [run, id, detail?.assignedUserId],
  );

  const returnToQueue = useCallback(async () => {
    const ok = await run("returnToQueue", async () => {
      await adminChat.returnToQueue(id);
      return null;
    }, CHAT_TEXT.toastReturned, "Не удалось вернуть диалог. Попробуйте ещё раз");
    if (ok) onReturned();
    return ok;
  }, [run, id, onReturned]);

  const unarchive = useCallback(
    () => run("unarchive", () => adminChat.unarchive(id), CHAT_TEXT.toastUnarchived, "Не удалось вернуть диалог из архива"),
    [run, id],
  );

  const archive = useCallback(async () => {
    if (!detail || busy) return false;
    setBusy("archive");
    try {
      setDetail(await adminChat.archive(id, guard));
      // «Диалог в архиве · [Вернуть]» — без подтверждения, отмена в сообщении.
      notify.success(CHAT_TEXT.toastArchived, {
        action: {
          label: CHAT_TEXT.toastArchivedUndo,
          onClick: () => {
            void adminChat.unarchive(id).then(setDetail).catch((e: unknown) => notify.error(chatErrorText(e, "Не удалось вернуть диалог из архива"))).finally(bumpData);
          },
        },
      });
      return true;
    } catch (error) {
      const race = raceDetails(error);
      if (race) {
        notify.warning(raceText(race));
        await refreshDetail();
      } else notify.error(chatErrorText(error, "Не удалось отправить диалог в архив"));
      return false;
    } finally {
      setBusy(null);
      bumpData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- guard собирается из detail
  }, [detail, busy, id, notify, setDetail, refreshDetail, bumpData]);

  return { busy, take, transfer, returnToQueue, archive, unarchive };
}

export type ConversationActionsApi = ReturnType<typeof useConversationActions>;
